import { AllocationTypes, AllocationStatuses } from '@/features/Collaborators/Models';
import type { AllocationStatus, AllocationType } from '@/features/Collaborators/Models';

/** Domínios de e-mail considerados quadro Raro na exportação de colaboradores. */
export const RARO_EMAIL_DOMAINS = ['@rarolabs.com.br', '@raroprestador.com.br'] as const;

/**
 * Tipos de alocação vinculados a cliente — os únicos que entram na exportação
 * de alocações por cliente/mês. Interno, Ausência, Comercial e Aviso Prévio
 * ficam de fora por não representarem escopo entregue a cliente.
 */
export const CLIENT_ALLOCATION_TYPES: readonly AllocationType[] = [
    AllocationTypes.BodyShop,
    AllocationTypes.OpenScope,
    AllocationTypes.ClosedScope,
    AllocationTypes.Support,
    AllocationTypes.Provisional,
];

/**
 * Tipos de alocação considerados esforço interno da Raro. Definem a situação
 * "Interno" na listagem de colaboradores.
 */
export const INTERNAL_ALLOCATION_TYPES: readonly AllocationType[] = [
    AllocationTypes.Operational,
    AllocationTypes.Commercial,
];

/**
 * Valores do campo `status` que caracterizam colaborador desativado.
 * "afastado" fica de fora de propósito: o vínculo segue ativo.
 */
export const INACTIVE_COLLABORATOR_STATUSES = ['inactive', 'inativo', 'desligado'] as const;

/**
 * Vínculo contratual a partir de `collaboratorType`. Celetista e Fornecedor
 * viram CLT e PJ, que é como a área de pessoas se refere a eles.
 */
export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
    Celetista: 'CLT',
    Fornecedor: 'PJ',
    Estagiário: 'Estagiário',
    Sócio: 'Sócio',
};

/** Alocações canceladas nunca representam pessoa alocada. */
export const EXCLUDED_ALLOCATION_STATUSES: readonly AllocationStatus[] = [
    AllocationStatuses.Cancelled,
];

/** Rótulo usado quando a alocação tem tipo de cliente mas nenhum cliente preenchido. */
export const UNKNOWN_CLIENT_LABEL = 'Sem cliente definido';

/** Rótulos de preenchimento no relatório detalhado, para manter o filtro do Excel consistente. */
export const UNKNOWN_PROJECT_LABEL = 'Sem projeto definido';
export const UNKNOWN_SCOPE_TYPE_LABEL = 'Sem tipo definido';
export const UNKNOWN_COLLABORATOR_LABEL = 'Colaborador sem nome';

/**
 * Carga mensal cheia de um colaborador, em horas. Mesmo valor usado pelo match
 * de vagas (`MONTHLY_FULL_LOAD`) e pela cloud function `matchCollaborators`.
 */
export const MONTHLY_FULL_LOAD_HOURS = 168;

/**
 * Conversões de bucket para horas/mês, espelhando `calculateMonthlyHours` da
 * cloud function `matchCollaborators` para que os dois lados deem o mesmo número.
 */
export const WORKING_DAYS_PER_MONTH = 21;
export const WEEKS_PER_MONTH = 4;
export const DAYS_PER_MONTH = 30;

/**
 * Prefixo dos tipos de recurso que caracterizam desenvolvedor — o catálogo usa
 * nomes como "Desenvolvedor Backend", "Desenvolvedor Frontend", "Desenvolvedor
 * Mobile". A comparação é feita sobre o nome normalizado (sem acento, minúsculo).
 */
export const DEVELOPER_RESOURCE_TYPE_PREFIX = 'desenvolvedor';

/**
 * Palavras-chave da iniciativa que marcam a alocação como Academy. Comparadas
 * contra `projectName` normalizado, por conter.
 */
export const ACADEMY_PROJECT_KEYWORDS = ['academy'] as const;

/**
 * Palavras-chave da iniciativa que marcam a alocação como projeto interno.
 * Hífens e pontuação viram espaço na normalização, então "raro-digital" casa
 * com "raro digital".
 */
export const INTERNAL_PROJECT_KEYWORDS = ['onvue', 'raro digital'] as const;

/**
 * Tipos de alocação que não consomem capacidade de trabalho e por isso não
 * entram nas horas alocadas.
 */
export const NON_WORKING_ALLOCATION_TYPES: readonly AllocationType[] = [
    AllocationTypes.Absence,
    AllocationTypes.Product,
];

/** Janela padrão do seletor: 11 meses atrás até 6 meses à frente do mês atual. */
export const DEFAULT_MONTHS_BACK = 11;
export const DEFAULT_MONTHS_AHEAD = 6;

/** Quantos colaboradores são lidos em paralelo ao varrer as subcoleções. */
export const ALLOCATION_FETCH_CONCURRENCY = 10;

export const XLSX_MIME_TYPE =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
